// Public (no-auth) gallery API — approved, event-linked photos only.
// Backed by GET /public/event-photos on the Event Images service.

const BASE_URL =
  process.env.REACT_APP_EVENT_IMAGES_API_URL ||
  "https://7vnhfarqxg.execute-api.us-east-1.amazonaws.com/prod";

export const S3_PHOTO_BASE = "https://s3.amazonaws.com/social-images-public/";

const THUMB_SUFFIX_RE = /\.(jpe?g|png|webp|gif)$/i;

// Small-thumbnail URL: swap the extension for `_thumb.jpg` (the
// thumbnailGen service keeps a downsized sibling for every photo).
export const buildThumbUrl = (s3Key) => {
  if (!s3Key) return "";
  return `${S3_PHOTO_BASE}${s3Key.replace(THUMB_SUFFIX_RE, "_thumb.jpg")}`;
};

export const buildOriginalUrl = (s3Key) => `${S3_PHOTO_BASE}${s3Key}`;

const get = async (url) => {
  const resp = await fetch(url);
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(
      (data && data.error) || `Request failed (${resp.status})`
    );
  }
  return data;
};

/**
 * Recent events, each with a small photo preview.
 * @returns {Promise<{events: Array<{socialEventId, title, date, photoCount, photos}>}>}
 */
export const fetchRecentEventPhotos = () =>
  get(`${BASE_URL.replace(/\/$/, "")}/public/event-photos`);

// --- Homepage fast path -----------------------------------------------
// The backend rewrites a static snapshot of the homepage payload to the
// public photo bucket on every photo/config change, so first paint can
// skip API Gateway + Lambda entirely. The API above stays the source of
// truth; the snapshot and localStorage only make the wait invisible.

const HOME_SNAPSHOT_URL = `${S3_PHOTO_BASE}main/home-photos.json`;
const HOME_CACHE_KEY = "homeRecentEventPhotos:v1";

/**
 * Static S3 snapshot of the homepage events. Throws if missing/invalid
 * — callers treat it as best-effort.
 */
export const fetchHomeSnapshot = () => get(HOME_SNAPSHOT_URL);

/**
 * Last events list this browser saw (survives reloads → instant paint,
 * titles included, before any network round-trip). Best-effort: storage
 * can be unavailable or hold garbage, so never throw.
 * @returns {Array|null}
 */
export const readCachedHomeEvents = () => {
  try {
    const raw = localStorage.getItem(HOME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed && parsed.events) ? parsed.events : null;
  } catch (e) {
    return null;
  }
};

export const writeCachedHomeEvents = (events) => {
  try {
    if (!Array.isArray(events)) return;
    localStorage.setItem(
      HOME_CACHE_KEY,
      JSON.stringify({ at: Date.now(), events })
    );
  } catch (e) {
    // Storage full / private mode — the cache is only an accelerator.
  }
};

/**
 * Every event that has approved photos (no homepage cap/exclusions) —
 * the "all event photos" page.
 * @returns {Promise<{events: Array<{socialEventId, title, date, photoCount, photos}>}>}
 */
export const fetchAllEventPhotos = () =>
  get(`${BASE_URL.replace(/\/$/, "")}/public/event-photos?all=1`);

/**
 * Every approved photo for one event.
 * @param {string} socialEventId
 * @returns {Promise<{event: {socialEventId, title, date, photoCount, photos}}>}
 */
export const fetchEventGallery = (socialEventId) =>
  get(
    `${BASE_URL.replace(/\/$/, "")}/public/event-photos?socialEventId=${encodeURIComponent(
      socialEventId
    )}`
  );

/**
 * Download a photo to the device. Fetches as a blob so the browser's
 * download path works on iPhone/Android too; falls back to opening the
 * image in a new tab (long-press → Save Image) if the fetch fails.
 * @param {string} s3Key
 * @param {string} [filename]
 */
export const downloadPhoto = async (s3Key, filename) => {
  const url = buildOriginalUrl(s3Key);
  const fallbackName =
    filename ||
    (s3Key || "photo").split("/").pop() ||
    "photo.jpg";
  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  } catch (e) {
    window.open(url, "_blank", "noopener");
  }
};

/**
 * Share a photo through the device's native share sheet (Web Share API).
 * On phones the sheet lists Instagram / Facebook / Messages, so this is
 * the one-tap "post it" path from the public gallery. Shares the actual
 * image file when the browser allows it, otherwise the photo's URL.
 * Never throws; returns one of:
 *   "shared"      — the sheet opened and the user picked a target
 *   "cancelled"   — the user dismissed the sheet
 *   "unsupported" — no Web Share API here (desktop browsers)
 *   "failed"      — the fetch or share call errored
 * @param {string} s3Key
 * @param {{title?: string, text?: string, filename?: string}} [opts]
 */
export const sharePhoto = async (s3Key, opts = {}) => {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
    return "unsupported";
  }
  const url = buildOriginalUrl(s3Key);
  const filename =
    opts.filename || (s3Key || "photo").split("/").pop() || "photo.jpg";
  const payload = { title: opts.title, text: opts.text };
  try {
    try {
      // navigator.share() must run inside the tap's user-activation
      // window (~5s in Chrome), so cap the image fetch: a slow download
      // degrades to sharing the URL instead of failing outright.
      const ctrl =
        typeof AbortController === "function" ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), 3500) : null;
      let resp;
      try {
        resp = await fetch(url, ctrl ? { signal: ctrl.signal } : undefined);
      } finally {
        if (timer) clearTimeout(timer);
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const file = new File([blob], filename, {
        type: blob.type || "image/jpeg",
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        payload.files = [file];
      }
    } catch (e) {
      // Couldn't fetch the bytes (or File isn't constructible) — fall
      // through and share the public URL instead.
    }
    if (!payload.files) payload.url = url;
    await navigator.share(payload);
    return "shared";
  } catch (e) {
    if (e && e.name === "AbortError") return "cancelled";
    return "failed";
  }
};
